import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Badge,
    Pagination, PaginationItem, PaginationLink, Label
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

// MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const StaffSalaryList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // --- Core States ---
    const [salaries, setSalaries] = useState([]);
    const [summary, setSummary] = useState({ total_records: 0, total_salary: 0, total_paid: 0, total_pending: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
    const [staffOptions, setStaffOptions] = useState([]);

    // --- Filter States ---
    const [filters, setFilters] = useState({
        staff_id: null,
        year: "",
        month: null,
        payment_status: null,
        from_date: null,
        to_date: null,
        page: 1,
        limit: 10
    });

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '45px',
            height: '45px',
            borderRadius: '8px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
        valueContainer: (provided) => ({ ...provided, height: '45px', padding: '0 6px' }),
        input: (provided) => ({ ...provided, margin: '0px' }),
        indicatorSeparator: () => ({ display: 'none' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '45px' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        fetchStaffList();
        loadSalaries();
    }, [filters.page, filters.staff_id, filters.payment_status, filters.month]);

    const fetchStaffList = async () => {
        try {
            const res = await Handler({ method: "GET", url: "/staff/list.php" });
            if (res?.success) {
                // 🔥 Machan, API-la nested structure (s.staff.name) irundha idhu correct-ah work aagum
                const options = res.data.map(s => ({
                    value: s.id,
                    label: s.staff?.name?.toUpperCase() || s.name?.toUpperCase() || "UNKNOWN STAFF"
                }));
                setStaffOptions(options);
            }
        } catch (err) {
            console.error("Staff fetch error:", err);
        }
    };

    const loadSalaries = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                ...filters,
                staff_id: filters.staff_id?.value || "",
                month: filters.month?.value || "",
                payment_status: filters.payment_status?.value || "",
                from_date: filters.from_date ? dayjs(filters.from_date).format('YYYY-MM-DD') : "",
                to_date: filters.to_date ? dayjs(filters.to_date).format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/salary/list.php?${queryParams}` });

            if (res?.success) {
                setSalaries(res.data || []);
                setSummary(res.summary);
                setPagination({
                    current_page: res.page,
                    total_pages: res.total_pages,
                    total_records: res.total
                });
            } else {
                toast.error(res?.message || "Failed to fetch salaries");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Date(0, i).toLocaleString('en-US', { month: 'long' })
    }));

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1 bg-light min-vh-100">
                <div className="page-header mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="fw-bold m-0 text-theme">Staff Payroll Management</h3>
                            <p className="text-muted small m-0">Track and manage monthly staff salaries and payments.</p>
                        </div>
                        <Button color="primary" className="px-4 shadow-sm" onClick={() => navigate("/salary/add")}>
                            <i className="bx bx-plus me-1"></i> Add New Salary
                        </Button>
                    </div>
                </div>

                {/* SUMMARY CARDS */}
                <Row className="mb-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-primary text-white text-center p-2">
                            <small className="opacity-75 fw-bold text-uppercase">Total Payroll</small>
                            <h4 className="fw-bold mb-0">₹{summary.total_salary?.toLocaleString()}</h4>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-success text-white text-center p-2">
                            <small className="opacity-75 fw-bold text-uppercase">Total Paid</small>
                            <h4 className="fw-bold mb-0">₹{summary.total_paid?.toLocaleString()}</h4>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-danger text-white text-center p-2">
                            <small className="opacity-75 fw-bold text-uppercase">Total Pending</small>
                            <h4 className="fw-bold mb-0">₹{summary.total_pending?.toLocaleString()}</h4>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm rounded-4 bg-dark text-white text-center p-2">
                            <small className="opacity-75 fw-bold text-uppercase">Records</small>
                            <h4 className="fw-bold mb-0">{summary.total_records}</h4>
                        </Card>
                    </Col>
                </Row>

                {/* FILTERS */}
                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="p-3">
                        <Row className="g-2 align-items-end">
                            <Col md={3}>
                                <Label className="small fw-bold">Staff Member</Label>
                                <Select
                                    options={staffOptions}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="Search Staff..."
                                    value={filters.staff_id}
                                    onChange={(opt) => setFilters({ ...filters, staff_id: opt, page: 1 })}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">Month</Label>
                                <Select
                                    options={monthOptions}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="Select"
                                    value={filters.month}
                                    onChange={(opt) => setFilters({ ...filters, month: opt, page: 1 })}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">Status</Label>
                                <Select
                                    options={[{ value: 'paid', label: 'Paid' }, { value: 'pending', label: 'Pending' }]}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="Select"
                                    value={filters.payment_status}
                                    onChange={(opt) => setFilters({ ...filters, payment_status: opt, page: 1 })}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">From Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.from_date}
                                    onChange={(val) => setFilters({ ...filters, from_date: val, page: 1 })}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">To Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.to_date}
                                    onChange={(val) => setFilters({ ...filters, to_date: val, page: 1 })}
                                    minDate={filters.from_date}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={1}>
                                <Button color="dark" block style={{ height: '45px' }} onClick={loadSalaries}><i className="bx bx-refresh"></i></Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* SALARY TABLE */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4">Staff Details</th>
                                    <th>Period</th>
                                    <th>Salary Split (Base / Bonus / Ded.)</th>
                                    <th className="text-center">Net Salary</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {salaries.length > 0 ? (
                                    salaries.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="ps-4">
                                                <div className="fw-bold text-dark">{item.staff.name}</div>
                                                <small className="text-muted">{item.staff.phone}</small>
                                            </td>
                                            <td>
                                                <div className="fw-bold">
                                                    {dayjs().month(item.salary.month - 1).format('MMM')} {item.salary.year}
                                                </div>
                                                <small className="text-muted">ID: #{item.id}</small>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <span className="text-success">₹{item.salary.base_salary}</span> /
                                                    <span className="text-primary"> ₹{item.salary.bonus}</span> /
                                                    <span className="text-danger"> ₹{item.salary.deduction}</span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="fw-bold text-theme">₹{item.salary.net_salary?.toLocaleString()}</div>
                                            </td>
                                            <td className="text-center">
                                                <Badge color={item.payment.status === 'paid' ? 'success' : 'warning'} pill>
                                                    {item.payment.status?.toUpperCase()}
                                                </Badge>
                                                {item.payment.paid_date && <div className="x-small text-muted mt-1">{item.payment.paid_date}</div>}
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    <Button color="light" size="sm" className="border shadow-sm" onClick={() => navigate(`/salary/${item.id}`)}>
                                                        <i className="bx bx-edit-alt text-info fs-5"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="6" className="py-5 text-center text-muted">No salary records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* PAGINATION */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>
                            {[...Array(pagination?.total_pages)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters.page} key={i}>
                                    <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default StaffSalaryList;